import { getCollisionStateConfig, isKnownCollisionState } from '@/components/collision/collisionStateConfig';
import { Tooltip } from 'primereact/tooltip';
import { type FC, useId } from 'react';
import { useTranslation } from 'react-i18next';

interface CollisionStatusTagProps {
  /** Collision simulation state from the backend; renders nothing when null/absent. */
  state: string | null | undefined;
}

/**
 * Compact collision-status badge for project list views (card + table row).
 *
 * Takes the shape of the app's `CHIP` — a full-radius pill — and the colour of the shared
 * collision state config, so the label, the icon and the verdict colour match the project-detail
 * panel while the geometry matches every other tag on the page.
 */
export const CollisionStatusTag: FC<CollisionStatusTagProps> = ({ state }) => {
  const { t } = useTranslation();
  // CSS-class-safe id so each tag instance gets its own PrimeReact tooltip target.
  const hintClass = `collision-status-hint-${useId().replace(/:/g, '')}`;

  if (!state) {
    return null;
  }

  const cfg = getCollisionStateConfig(state);
  const label = isKnownCollisionState(state) ? t(`projectDetail.collisionStates.${state}`, state) : state;
  const Icon = cfg.icon;
  const tooltip = t('projectDetail.collisionDetectionState');

  return (
    <>
      <Tooltip target={`.${hintClass}`} position="top" />
      <span
        className={`${hintClass} inline-flex cursor-help items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${cfg.tagBg} ${cfg.tagText}`}
        data-pr-tooltip={tooltip}
      >
        <Icon size={12} className={state === 'running' ? 'shrink-0 animate-spin' : 'shrink-0'} aria-hidden="true" />
        <span className="max-w-[120px] truncate">{label}</span>
      </span>
    </>
  );
};
